// @flow

import React, { type Element, useCallback, useState, useContext } from 'react'
import { Grid, Box, Button, CircularProgress } from '@material-ui/core'
import FacilityCodeField from './FacilityCodeField'
import FacilityNameField from './FacilityNameField'
import ValidationResult from './ValidationResult'
import FormData from './FormData'
import { getCsrfToken, request } from '../../../Common/Request'
import { UserContext } from '../../../Common/UserContext'
import type { PrivateFacility } from '../../types'

type Props = {|
  handleServerErrors: ({ [string]: Array<string> }) => void,
  createdFacilities: Array<PrivateFacility>,
  setCreatedFacilities: (any) => void,
  closeModal: () => void,
  showSuccess: () => void,
|}

function FacilityCreationForm({
  handleServerErrors,
  createdFacilities,
  setCreatedFacilities,
  closeModal,
  showSuccess,
}: Props): Element<typeof React.Fragment> {
  const [validationResult, setValidationResult] = useState(new ValidationResult())
  const [formData, setFormData] = useState(new FormData())
  const [errors, setErrors] = useState(new FormData())
  const [isLoading, setIsLoading] = useState(false)
  const userContext = useContext(UserContext)

  const handleValidationResultChange = (fieldName: $Keys<ValidationResult>, isValid: boolean) => {
    setValidationResult((prevValue) => prevValue.update(fieldName, isValid))
  }
  const handleFormDataChange = (fieldName: $Keys<FormData>, value: string) => {
    setFormData((prevValue) => prevValue.update(fieldName, value))
  }

  const handleErrorsChange = useCallback((fieldName: $Keys<FormData>, value: string) => {
    setErrors((prevValue) => prevValue.update(fieldName, value))
  }, [])

  const handleSuccess = (data) => {
    setIsLoading(false)
    setFormData((prevValue) => prevValue.update('facilityCode', ''))
    setFormData((prevValue) => prevValue.update('facilityName', ''))
    showSuccess()
    const updatedFacilities = createdFacilities.concat([
      { id: data.id, code: data.code, name: data.name, private: true },
    ])
    setCreatedFacilities(updatedFacilities)
    closeModal()
  }

  function handleErrors(errors: { [string]: Array<string> }): void {
    setIsLoading(false)

    if ('code' in errors || 'name' in errors) {
      if ('code' in errors) {
        handleErrorsChange('facilityCode', errors.code[0])
        handleValidationResultChange('facilityCode', false)
      }
      if ('name' in errors) {
        handleErrorsChange('facilityName', errors.name[0])
        handleValidationResultChange('facilityName', false)
      }
    } else {
      handleServerErrors(errors)
    }
  }

  async function submitForm(): Promise<boolean> {
    setIsLoading(true)

    const payload = JSON.stringify({
      code: formData.facilityCode,
      name: formData.facilityName,
    })

    const path = userContext.createFacilityPath()
    const headers = { 'X-CSRF-Token': getCsrfToken() }

    return await request(path, 'POST', headers, payload, handleErrors, handleSuccess)
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mb={2}>
          <FacilityCodeField
            value={formData.facilityCode}
            setIsValid={(isValid) => handleValidationResultChange('facilityCode', isValid)}
            setValue={(value) => handleFormDataChange('facilityCode', value)}
            serverError={errors.facilityCode}
            setServerError={handleErrorsChange}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box mb={3}>
          <FacilityNameField
            value={formData.facilityName}
            setIsValid={(isValid) => handleValidationResultChange('facilityName', isValid)}
            setValue={(value) => handleFormDataChange('facilityName', value)}
            serverError={errors.facilityName}
            setServerError={handleErrorsChange}
          />
        </Box>
      </Grid>
      <Grid container direction="row" justify="flex-end" alignItems="center">
        <Button onClick={closeModal} color="primary">
          Cancel
        </Button>
        <Box ml={3} display="inline-block" position="relative">
          <Button
            variant="contained"
            color="primary"
            disabled={!validationResult.isValid() || isLoading}
            onClick={submitForm}
          >
            Submit
          </Button>
          {isLoading && (
            <CircularProgress
              size={24}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: -12,
                marginLeft: -12,
              }}
            />
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default FacilityCreationForm
